import React, { useState, useEffect, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { User, Bot, TrendingUp, AlertCircle, FileText } from 'lucide-react';

const MessageBubble = forwardRef(({ message }, ref) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (message.sender === 'assistant' && message.text) {
      setIsTyping(true);
      setDisplayedText('');
      
      let i = 0;
      const text = message.text;
      const typingSpeed = 30; // milliseconds per character
      
      const typeWriter = () => {
        if (i < text.length) {
          setDisplayedText(text.slice(0, i + 1));
          i++;
          setTimeout(typeWriter, typingSpeed);
        } else {
          setIsTyping(false);
        }
      };
      
      typeWriter();
    } else {
      setDisplayedText(message.text);
      setIsTyping(false);
    }
  }, [message.text, message.sender]);

  const getIcon = () => {
    if (message.sender === 'user') return <User size={14} />;
    if (message.sender === 'assistant') return <Bot size={14} />;
    return null;
  };

  const getDataBadge = () => {
    if (!message.data) return null;
    
    return (
      <div className="message-data-badge">
        <TrendingUp size={12} />
        <span>Data Available</span>
      </div>
    );
  };

  /**
   * Process text and convert [USER_MANUAL_LINK] to clickable links
   */
  const processTextWithLinks = (text) => {
    if (!text) return null;
    
    // Check if text contains the placeholder
    if (!text.includes('[USER_MANUAL_LINK]')) {
      return text;
    }

    // Split by the placeholder
    const parts = text.split('[USER_MANUAL_LINK]');
    
    return (
      <>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 && (
              <a 
                href="/user-manual"  // ← UPDATED: Changed to /user-manual
                target="_blank" 
                rel="noopener noreferrer"
                className="user-manual-link"
                onClick={(e) => handleManualClick(e)}
                style={{
                  color: '#0066cc',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: '500',
                  margin: '0 2px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FileText size={14} />
                User Manual
              </a>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  /**
   * Handle manual link click with error handling
   */
  const handleManualClick = (e) => {
    e.preventDefault();
    
    // Option 1: Direct navigation (simpler, recommended)
    window.open('/user-manual', '_blank', 'noopener,noreferrer');
    
    // Option 2: With availability check (optional, uncomment if you want validation)
    /*
    fetch('/api/v1/user-manual/health')
      .then(response => response.json())
      .then(data => {
        if (data.pdf_available) {
          window.open('/user-manual', '_blank', 'noopener,noreferrer');
        } else {
          alert('User manual is currently being prepared. Please try again in a moment.');
        }
      })
      .catch(error => {
        console.error('Error checking manual availability:', error);
        // Still try to open
        window.open('/user-manual', '_blank', 'noopener,noreferrer');
      });
    */
  };

  // Try to render structured content when available
  const renderStructured = () => {
    let data = message?.data;
    // If no explicit data, try to parse JSON from text
    if (!data && typeof message?.text === 'string') {
      const t = message.text.trim();
      if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
        try { data = JSON.parse(t); } catch (e) { /* ignore */ }
      }
    }
    if (!data) return null;

    // Format numbers
    const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
    const formatVal = (v) => {
      if (typeof v === 'number') return nf.format(v);
      return String(v);
    };

    // Array of records => table
    if (Array.isArray(data) && data.length && typeof data[0] === 'object') {
      const cols = Array.from(
        data.reduce((set, row) => {
          Object.keys(row || {}).forEach(k => set.add(k));
          return set;
        }, new Set())
      );
      return (
        <div className="structured-table">
          <table>
            <thead>
              <tr>
                {cols.map(c => <th key={c}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  {cols.map(c => <td key={c}>{formatVal(row?.[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Object => key/value table
    if (data && typeof data === 'object') {
      return (
        <div className="structured-table">
          <table>
            <tbody>
              {Object.entries(data).map(([k, v]) => (
                <tr key={k}>
                  <th>{k}</th>
                  <td>{formatVal(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <motion.div
      ref={ref}
      className={`va-message ${message.sender}`}
      initial={{ opacity: 0, x: message.sender === 'user' ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8 }}
    >
      <div className="message-avatar">
        {getIcon()}
      </div>
      
      <div className="message-content">
        {renderStructured() ? (
          renderStructured()
        ) : (
          <div className="message-text">
            {message.streaming ? (
              <>
                {processTextWithLinks(message.text)}
                <span className="typing-cursor">|</span>
              </>
            ) : (
              <>
                {processTextWithLinks(displayedText)}
                {isTyping && <span className="typing-cursor">|</span>}
              </>
            )}
          </div>
        )}
        {getDataBadge()}
        <div className="message-time">{message.timestamp}</div>
      </div>
    </motion.div>
  );
});

export default MessageBubble;